<template>
  <div class="chart-container">
    <canvas ref="canvas" />
  </div>
</template>

<script>
import { customAxios } from "@/utils/customAxios";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

// Default glucose zones (lower bound exclusive except low, upper inclusive).
//   low          0  <= v <=  80
//   very_healthy 80 <  v <= 130
//   healthy      130<  v <= 150
//   caution      150<  v <= 180
//   high             v >  180
const ZONES = [
  { key: "low", label: "Low (0–80)", color: "#FFB300" },
  { key: "very_healthy", label: "Very Healthy (80–130)", color: "#388E3C" },
  { key: "healthy", label: "Healthy (130–150)", color: "#8BC34A" },
  { key: "caution", label: "Caution (150–180)", color: "#F57C00" },
  { key: "high", label: "High (>180)", color: "#D32F2F" },
];

export default {
  name: "DonutGlucoseZones",
  data() {
    return {
      total: 0,
      counts: ZONES.reduce((m, z) => ((m[z.key] = 0), m), {}),
      chart: null,
    };
  },
  mounted() {
    this.fetchData();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
  methods: {
    async fetchData() {
      try {
        const res = await customAxios.get("/admin/glucose-readings/stats");
        const body = res.data || {};
        this.total = Number(body.total) || 0;
        const zones = body.zones || {};
        for (const z of ZONES) {
          this.counts[z.key] = Number(zones[z.key]) || 0;
        }
        this.$nextTick(this.renderChart);
      } catch (err) {
        console.error("Failed to load glucose stats", err);
      }
    },
    renderChart() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      if (this.chart) this.chart.destroy();
      const stale = Chart.getChart(canvas);
      if (stale) stale.destroy();

      const total = this.total;
      const data = ZONES.map((z) => this.counts[z.key]);

      this.chart = new Chart(canvas, {
        type: "doughnut",
        data: {
          labels: ZONES.map((z) => z.label),
          datasets: [
            {
              data,
              backgroundColor: ZONES.map((z) => z.color),
              borderColor: "#ffffff",
              borderWidth: 2,
              hoverOffset: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "72%",
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                boxWidth: 10,
                boxHeight: 10,
                padding: 10,
                font: { size: 11 },
                color: "#5b6577",
                usePointStyle: true,
                pointStyle: "circle",
              },
            },
            tooltip: {
              backgroundColor: "#1f2540",
              titleColor: "#ffffff",
              bodyColor: "#ffffff",
              padding: 10,
              cornerRadius: 8,
              callbacks: {
                label: (ctx) => {
                  const v = ctx.raw || 0;
                  const pct = total ? ((v / total) * 100).toFixed(1) : "0.0";
                  return `${ctx.label}: ${v} (${pct}%)`;
                },
              },
            },
          },
        },
        plugins: [
          {
            id: "centerLabel",
            beforeDraw: (chart) => {
              const { ctx, chartArea } = chart;
              if (!chartArea) return;
              const cx = (chartArea.left + chartArea.right) / 2;
              const cy = (chartArea.top + chartArea.bottom) / 2;
              ctx.save();
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillStyle = "#1f2540";
              ctx.font = "700 28px 'Kumbh Sans', system-ui, sans-serif";
              ctx.fillText(total.toLocaleString(), cx, cy - 8);
              ctx.font = "500 11px 'Kumbh Sans', system-ui, sans-serif";
              ctx.fillStyle = "#6b7383";
              ctx.fillText("READINGS", cx, cy + 14);
              ctx.restore();
            },
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
}
canvas {
  width: 100% !important;
  height: 100% !important;
}
</style>
